import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { authApi } from '../api/auth';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';

function errorText(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return 'Could not set your new password. Please try again.';
}

/**
 * Forced password reset screen.
 *
 * Reached after a support-login (or a normal login) when the account is flagged
 * `must_reset_password`. It is intentionally outside the app shell and offers no
 * navigation into the app; the only ways out are a successful reset or signing
 * out. The router guard bounces any manual attempt to load /app back here.
 */
export default function ResetPasswordPage() {
  const { user, logout, applySession } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Your new password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await authApi.resetPassword(password);
      // Fresh token + flag cleared; update state before entering the app so the
      // guard does not bounce us straight back to this screen.
      applySession(data.user, data.token);
      navigate('/app/chat', { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-950">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="text-center">
          <KeyRound className="mx-auto text-brand-600" size={40} />
          <h1 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">Set a new password</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            For your security, you must choose a new password before continuing.
            {user?.email ? <> Your account: <span className="font-medium">{user.email}</span></> : null}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="reset-password-form">
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              minLength={6}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              minLength={6}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </div>

          {password.length > 0 && confirm.length > 0 && password === confirm && password.length >= 6 && (
            <p className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 size={14} /> Passwords match
            </p>
          )}

          {error && (
            <p role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
          >
            {submitting ? <LoadingSpinner size="sm" /> : <KeyRound size={16} />}
            {submitting ? 'Saving…' : 'Set password and continue'}
          </button>
        </form>

        <button
          type="button"
          onClick={logout}
          className="mt-4 w-full text-center text-xs text-slate-500 underline-offset-2 hover:underline dark:text-slate-400"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
